import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button, FormSaveBar, Input, Select } from '../ui';
import { PrivateUseOverrideField } from './PrivateUseOverrideField';
import { useSuppliers } from '../../services/api/suppliers';
import { CategoryComboboxWithCreate } from '../categories/CategoryComboboxWithCreate';
import { CategorySuggestion } from './CategorySuggestion';
import { formatCurrency, type ExpenseDetailDto } from '@armard/shared';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { expenseDraft, expenseDraftError, type ExpenseDraft } from './expense-draft';
export type ExpenseFormSubmit = ExpenseDraft;
export interface ExpenseFormProps {
  mode: 'create' | 'edit';
  initial?: Partial<ExpenseDetailDto>;
  submitting?: boolean;
  onSubmit: (payload: ExpenseDraft) => void;
  submitLabel: string;
  cancelHref: string;
  onDraftChange?: (payload: ExpenseDraft) => void;
  /** Optional controlled draft used by the review autosave surface. */
  draftOverride?: ExpenseDraft;
  hideActions?: boolean;
}
export function ExpenseForm({
  mode,
  initial,
  submitting,
  onSubmit,
  submitLabel,
  cancelHref,
  onDraftChange,
  draftOverride,
  hideActions = false,
}: ExpenseFormProps) {
  const { data: suppliers } = useSuppliers({ limit: 100 });
  const [localDraft, setLocalDraft] = useState(() => expenseDraft(initial));
  const draft = draftOverride ?? localDraft;
  const [error, setError] = useState<string | null>(null);
  const prefix = useId();
  useUnsavedChanges(draft, undefined, !hideActions);
  const callback = useRef(onDraftChange);
  callback.current = onDraftChange;
  useEffect(() => {
    if (!draftOverride) callback.current?.(draft);
  }, [draft, draftOverride]);
  function setDraft(next: ExpenseDraft | ((previous: ExpenseDraft) => ExpenseDraft)) {
    const nextDraft = typeof next === 'function' ? next(draft) : next;
    if (draftOverride) callback.current?.(nextDraft);
    else setLocalDraft(nextDraft);
  }
  function set<K extends keyof ExpenseDraft>(key: K, value: ExpenseDraft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }
  function item(index: number, patch: Partial<ExpenseDraft['items'][number]>) {
    set(
      'items',
      draft.items.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  }
  const totals = useMemo(
    () =>
      draft.items.reduce(
        (sum, row) => {
          const net = Math.round(row.quantity * row.unitPriceNet * 100) / 100;
          return { net: sum.net + net, vat: sum.vat + Math.round(net * row.vatRateAtSave) / 100 };
        },
        { net: 0, vat: 0 }
      ),
    [draft.items]
  );
  const incomplete =
    !draft.supplierId ||
    !draft.categoryId ||
    !draft.invoiceDate ||
    !draft.items.length ||
    (!draft.isKleinbetragsrechnung && !draft.supplierInvoiceNumber);
  return (
    <form
      className="max-w-4xl space-y-6"
      data-testid={`expense-form-${mode}`}
      onSubmit={(event) => {
        event.preventDefault();
        const invalid = expenseDraftError(draft, mode);
        setError(invalid);
        if (!invalid) onSubmit(draft);
      }}
    >
      <fieldset disabled={submitting} className="space-y-6 min-w-0">
        <section className="space-y-4">
          <h2 className="text-lg text-white">Belegdaten</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Lieferant"
              value={draft.supplierId ?? ''}
              onChange={(e) => set('supplierId', e.target.value || null)}
              options={[
                { value: '', label: 'Bitte wählen' },
                ...(suppliers?.items ?? []).map((s) => ({ value: s.id, label: s.displayName })),
              ]}
              data-testid="expense-form-supplier"
            />
            <div data-testid="expense-form-category">
              <CategoryComboboxWithCreate
                value={draft.categoryId ?? ''}
                onChange={(next) => set('categoryId', next || null)}
              />
              {!draft.categoryId && initial?.categorySuggestion && (
                <div className="mt-1">
                  <CategorySuggestion
                    suggestion={initial.categorySuggestion}
                    onAccept={(id) => set('categoryId', id)}
                    testid="expense-form-category-suggestion"
                  />
                </div>
              )}
            </div>
            <Input
              label="Rechnungsnummer Lieferant"
              value={draft.supplierInvoiceNumber ?? ''}
              onChange={(e) => set('supplierInvoiceNumber', e.target.value || null)}
              data-testid="expense-form-invoice-number"
            />
            <Input
              label="Kundenreferenz"
              value={draft.customerReference ?? ''}
              onChange={(e) => set('customerReference', e.target.value || null)}
            />
            <Input
              label="Rechnungsdatum"
              type="date"
              required={mode === 'create' || !!initial?.invoiceDate}
              value={draft.invoiceDate ?? ''}
              onChange={(e) =>
                set('invoiceDate', e.target.value || (initial?.invoiceDate ? '' : undefined))
              }
              data-testid="expense-form-invoice-date"
            />
            <Input
              label="Fällig am"
              type="date"
              value={draft.dueDate ?? ''}
              onChange={(e) => set('dueDate', e.target.value || null)}
            />
            <Input
              label="Bezahlt am"
              type="date"
              value={draft.paymentDate ?? ''}
              onChange={(e) => set('paymentDate', e.target.value || null)}
              hint="Der Belegstatus wird separat bestätigt."
            />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/80">
            {(
              [
                ['isKleinbetragsrechnung', 'Kleinbetragsrechnung'],
                ['reverseCharge', 'Reverse Charge'],
                ['supplierKleinunternehmer', 'Lieferant ist Kleinunternehmer'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 min-h-11">
                <input
                  type="checkbox"
                  checked={draft[key]}
                  onChange={(e) => set(key, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
        </section>
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg text-white">Positionen</h2>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                set('items', [
                  ...draft.items,
                  {
                    position: draft.items.length + 1,
                    description: '',
                    quantity: 1,
                    unit: null,
                    unitPriceNet: 0,
                    vatRateAtSave: 19,
                  },
                ])
              }
            >
              Position hinzufügen
            </Button>
          </div>
          <p className="text-sm text-white/50">
            Preise netto. Steuersätze und Einheiten bleiben je Position erhalten; negative Beträge
            sind möglich.
          </p>
          {draft.items.map((row, index) => (
            <div
              key={index}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3"
              data-testid={`expense-item-${index}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/60">Position {index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Position ${index + 1} entfernen`}
                  onClick={() =>
                    set(
                      'items',
                      draft.items
                        .filter((_, i) => i !== index)
                        .map((r, i) => ({ ...r, position: i + 1 }))
                    )
                  }
                >
                  Entfernen
                </Button>
              </div>
              <Input
                label="Beschreibung"
                required
                value={row.description}
                onChange={(e) => item(index, { description: e.target.value })}
              />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Input
                  label="Menge"
                  type="number"
                  min="0.0001"
                  step="any"
                  required
                  value={row.quantity}
                  onChange={(e) => item(index, { quantity: Number(e.target.value) })}
                />
                <Input
                  label="Einheit"
                  value={row.unit ?? ''}
                  maxLength={20}
                  placeholder="z. B. Stunden"
                  onChange={(e) => item(index, { unit: e.target.value || null })}
                />
                <Input
                  label="Einzelpreis netto"
                  type="number"
                  step="any"
                  required
                  value={row.unitPriceNet}
                  onChange={(e) => item(index, { unitPriceNet: Number(e.target.value) })}
                />
                <Input
                  label="USt. %"
                  type="number"
                  min={0}
                  max={30}
                  step="any"
                  required
                  value={row.vatRateAtSave}
                  onChange={(e) => item(index, { vatRateAtSave: Number(e.target.value) })}
                />
              </div>
            </div>
          ))}
          <div className="flex flex-wrap justify-end gap-x-6 gap-y-2 border-t border-white/10 pt-4 text-sm text-white/70">
            <span>Netto {formatCurrency(totals.net)}</span>
            <span>USt. {formatCurrency(totals.vat)}</span>
            <strong className="text-white">Brutto {formatCurrency(totals.net + totals.vat)}</strong>
          </div>
        </section>
        <PrivateUseOverrideField
          value={draft.privateUseOverridePercent}
          reason={draft.privateUseReason}
          fallbackDeductiblePercent={
            initial?.deductiblePercentAtSave == null
              ? null
              : Number(initial.deductiblePercentAtSave)
          }
          onChange={(next) =>
            setDraft((prev) => ({
              ...prev,
              privateUseOverridePercent: next.percent,
              privateUseReason: next.reason,
            }))
          }
        />
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(['notes', 'internalNotes'] as const).map((key) => (
            <div key={key}>
              <label
                htmlFor={`${prefix}-${key}`}
                className="block text-sm font-medium text-white/70 mb-1.5"
              >
                {key === 'notes' ? 'Notiz' : 'Interne Notiz'}
              </label>
              <textarea
                id={`${prefix}-${key}`}
                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white"
                rows={3}
                value={draft[key] ?? ''}
                onChange={(e) => set(key, e.target.value || null)}
              />
            </div>
          ))}
        </section>
      </fieldset>
      {mode === 'create' && incomplete && (
        <p className="text-sm text-amber-200/80">
          Dieser Beleg wird als Entwurf gespeichert. Für die vollständige Anlage werden Lieferant,
          Kategorie, Rechnungsdatum, mindestens eine Position und die Rechnungsnummer benötigt (bei
          Kleinbetragsrechnungen entfällt die Nummer).
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      )}
      {!hideActions && (
        <FormSaveBar>
          <Link to={cancelHref}>
            <Button type="button" variant="ghost">
              Abbrechen
            </Button>
          </Link>
          <Button type="submit" disabled={submitting}>
            {submitting
              ? 'Speichern…'
              : mode === 'create' && incomplete
                ? 'Als Entwurf speichern'
                : submitLabel}
          </Button>
        </FormSaveBar>
      )}
    </form>
  );
}
